import React from 'react';
import { Check, Star, Globe, Palette, TrendingUp, Instagram } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import AnimatedText from '../components/AnimatedText';
import FloatingCard from '../components/FloatingCard';
import ShimmerButton from '../components/ShimmerButton';
import { Link } from 'react-router-dom';

const Plans = () => {
  // Formules Réseaux Sociaux (sans prix)
  const socialPlans = [
    {
      id: 1,
      name: "Débutant",
      description: "Parfait pour les petites entreprises qui débutent avec une communication professionnelle.",
      features: [
        "Gestion des réseaux sociaux standard",
        "12 vidéos courtes par mois",
        "6 publications par mois"
      ],
      popular: false,
      color: "border-gray-600"
    },
    {
      id: 2,
      name: "Professionnel",
      description: "Solutions de communication complètes pour les entreprises en croissance.",
      features: [
        "20 vidéos courtes",
        "10 publications",
        "2 créations print",
        "1 analyse concurrentielle"
      ],
      popular: true,
      color: "border-amber-400"
    },
    {
      id: 3,
      name: "Entreprise",
      description: "Solutions de communication complètes pour les entreprises établies.",
      features: [
        "30 vidéos courtes",
        "20 publications",
        "5 créations print",
        "SEO sur site",
        "Développement de marque",
        "Analyse concurrentielle",
        "Gestion d'événements",
        "Partenariats + promotion sur nos canaux"
      ],
      popular: false,
      color: "border-gray-600"
    }
  ];

  // Formules Site Web (sans prix)
  const webPackages = [
    {
      id: 1,
      name: "Site Vitrine",
      description: "Solution idéale pour présenter votre activité avec une présence web professionnelle.",
      features: [
        "1 page vitrine complète",
        "Page contact intégrée",
        "SEO optimisé",
        "Design personnalisé",
        "1 vidéo gratuite offerte",
        "Photos professionnelles incluses"
      ],
      color: "border-blue-400",
      accentColor: "from-blue-600 to-indigo-600",
      popular: false
    },
    {
      id: 2,
      name: "Site Élaboré",
      description: "Site web complet avec toutes les pages essentielles pour développer votre activité.",
      features: [
        "2 à 5 pages personnalisées",
        "Page contact ou réservation",
        "Page à propos",
        "Page équipe",
        "Page services ou produits",
        "Photos professionnelles incluses",
        "1 vidéo gratuite offerte",
        "SEO optimisé par IA",
        "Optimisation client",
        "Conseil en communication"
      ],
      color: "border-amber-400",
      accentColor: "from-amber-600 to-orange-600",
      popular: true
    },
    {
      id: 3,
      name: "Site E-commerce",
      description: "Solution e-commerce complète pour vendre en ligne avec stratégie d'acquisition client.",
      features: [
        "Page vitrine",
        "Page contact",
        "Page nos produits",
        "Pages produits optimisées pour la vente",
        "Page de paiement sécurisée",
        "Entonnoir d'acquisition client",
        "SEO optimisé par IA"
      ],
      color: "border-green-400",
      accentColor: "from-green-600 to-teal-600",
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Section Hero - Dark Mode */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.1),transparent_70%)]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <AnimatedText 
              className="text-4xl sm:text-5xl font-bold text-white mb-6"
              isH1={true}
            >
              Choisissez Votre <span className="title-gradient">Formule Parfaite</span>
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-gray-300 leading-relaxed font-inter"
              delay={200}
            >
              Sélectionnez la solution de communication qui correspond à vos besoins. Toutes les formules incluent notre engagement d&apos;excellence et un support personnalisé.
            </AnimatedText>
          </div>
        </div>
      </section>

      {/* Section Formules Réseaux Sociaux */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                <Instagram className="w-8 h-8 text-white" />
              </div>
            </div>
            <AnimatedText 
              className="text-3xl sm:text-4xl font-inter font-bold text-white mb-4"
              isTitle={true}
            >
              Formules Réseaux Sociaux
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-gray-400 max-w-3xl mx-auto font-inter"
              delay={200}
            >
              Des solutions complètes pour votre communication digitale et votre présence sur les réseaux sociaux.
            </AnimatedText>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {socialPlans.map((plan, index) => (
              <AnimatedText key={plan.id} delay={300 + index * 200}>
                <FloatingCard 
                  className={`relative group hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-300 border-2 bg-gray-800 ${plan.color} ${
                    plan.popular ? 'scale-105 shadow-xl shadow-amber-500/20' : ''
                  }`}
                  delay={index * 0.3}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 text-sm font-medium font-inter">
                        <Star className="w-4 h-4 mr-1 fill-current" />
                        Plus Populaire
                      </Badge>
                    </div>
                  )}
                  
                  <CardHeader className="pb-8">
                    <div className="text-center">
                      <AnimatedText 
                        className="text-2xl font-inter font-bold text-white mb-4"
                        isTitle={true}
                        delay={400 + index * 200}
                      >
                        {plan.name}
                      </AnimatedText>
                      <AnimatedText 
                        className="text-gray-400 text-sm leading-relaxed font-inter"
                        delay={600 + index * 200}
                      >
                        {plan.description}
                      </AnimatedText>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <ul className="space-y-4 mb-8">
                      {plan.features.map((feature, featureIndex) => (
                        <AnimatedText 
                          key={featureIndex} 
                          delay={700 + index * 200 + featureIndex * 50}
                        >
                          <li className="flex items-center space-x-3">
                            <Check className="w-5 h-5 text-amber-500 flex-shrink-0" />
                            <span className="text-gray-300 font-inter">{feature}</span>
                          </li>
                        </AnimatedText>
                      ))}
                    </ul>
                    
                    <AnimatedText delay={800 + index * 200}>
                      <ShimmerButton 
                        asChild
                        className={`w-full py-6 text-lg font-medium font-inter transition-all duration-200 rounded-full ${
                          plan.popular 
                            ? 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white' 
                            : 'bg-gray-700 text-white hover:bg-gray-600'
                        }`}
                      >
                        <Link to="/contact">Demander un Devis</Link>
                      </ShimmerButton>
                    </AnimatedText>
                  </CardContent>
                </FloatingCard>
              </AnimatedText>
            ))}
          </div>
        </div>
      </section>

      {/* Section Formules Site Web */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                <Globe className="w-8 h-8 text-white" />
              </div>
            </div>
            <AnimatedText 
              className="text-3xl sm:text-4xl font-inter font-bold text-white mb-4"
              isTitle={true}
            >
              Formules Site Web
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-gray-400 max-w-3xl mx-auto font-inter"
              delay={200}
            >
              Solutions complètes pour votre présence en ligne avec optimisation SEO pilotée par IA.
            </AnimatedText>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {webPackages.map((pkg, index) => (
              <AnimatedText key={pkg.id} delay={300 + index * 200}>
                <FloatingCard 
                  className={`relative group hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-300 border-2 bg-gray-900 ${pkg.color} ${
                    pkg.popular ? 'scale-105 shadow-xl shadow-amber-500/20' : ''
                  }`}
                  delay={index * 0.3}
                >
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 text-sm font-medium font-inter">
                        <Star className="w-4 h-4 mr-1 fill-current" />
                        Recommandé
                      </Badge>
                    </div>
                  )}
                  
                  <CardHeader className="pb-8">
                    <div className="text-center">
                      <div className="mb-4">
                        {pkg.name === 'Site Vitrine' && <Globe className="w-12 h-12 text-blue-400 mx-auto mb-4" />}
                        {pkg.name === 'Site Élaboré' && <Palette className="w-12 h-12 text-amber-400 mx-auto mb-4" />}
                        {pkg.name === 'Site E-commerce' && <TrendingUp className="w-12 h-12 text-green-400 mx-auto mb-4" />}
                      </div>
                      <AnimatedText 
                        className="text-2xl font-inter font-bold text-white mb-4"
                        isTitle={true}
                        delay={400 + index * 200}
                      >
                        {pkg.name}
                      </AnimatedText>
                      <AnimatedText 
                        className="text-gray-400 text-sm leading-relaxed font-inter"
                        delay={600 + index * 200}
                      >
                        {pkg.description}
                      </AnimatedText>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <ul className="space-y-3 mb-8">
                      {pkg.features.map((feature, featureIndex) => (
                        <AnimatedText 
                          key={featureIndex} 
                          delay={700 + index * 200 + featureIndex * 50}
                        >
                          <li className="flex items-start space-x-3">
                            <Check className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-300 font-inter text-sm">{feature}</span>
                          </li>
                        </AnimatedText>
                      ))}
                    </ul>
                    
                    <AnimatedText delay={800 + index * 200}>
                      <ShimmerButton 
                        asChild
                        className={`w-full py-6 text-lg font-medium font-inter transition-all duration-200 rounded-full bg-gradient-to-r ${pkg.accentColor} hover:opacity-90 text-white`}
                      >
                        <Link to="/contact">Demander un Devis</Link>
                      </ShimmerButton>
                    </AnimatedText>
                  </CardContent>
                </FloatingCard>
              </AnimatedText>
            ))}
          </div>
          
          <AnimatedText delay={1000}>
            <div className="text-center mt-12">
              <p className="text-gray-400 font-inter mb-4">
                Tous les packages web incluent une optimisation SEO pilotée par IA
              </p>
            </div>
          </AnimatedText>
        </div>
      </section>

      {/* Section Tous les Forfaits Incluent */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <AnimatedText 
              className="text-3xl sm:text-4xl font-inter font-bold text-white mb-4"
              isTitle={true}
            >
              Tous les Forfaits Incluent
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-gray-400 max-w-3xl mx-auto font-inter"
              delay={200}
            >
              Chaque forfait comprend notre engagement fondamental pour la qualité et le support.
            </AnimatedText>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: "Équipe Experte", description: "Accès à nos professionnels expérimentés" },
              { title: "Garantie Qualité", description: "Nous soutenons chaque projet que nous livrons" },
              { title: "Approche Flexible", description: "Solutions personnalisées pour vos besoins uniques" },
              { title: "Support Continu", description: "Assistance continue après l'achèvement du projet" }
            ].map((feature, index) => (
              <AnimatedText key={index} delay={300 + index * 100}>
                <FloatingCard className="bg-gray-800 border border-gray-700 text-center hover:border-amber-500/50 transition-all duration-300" delay={index * 0.2}>
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-white" />
                    </div>
                    <AnimatedText 
                      className="text-xl font-inter font-semibold text-white mb-3"
                      isTitle={true}
                      delay={400 + index * 100}
                    >
                      {feature.title}
                    </AnimatedText>
                    <AnimatedText 
                      className="text-gray-400 font-inter"
                      delay={500 + index * 100}
                    >
                      {feature.description}
                    </AnimatedText>
                  </CardContent>
                </FloatingCard>
              </AnimatedText>
            ))}
          </div>
        </div>
      </section>

      {/* Section Appel à l'Action */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <AnimatedText 
              className="text-3xl sm:text-4xl font-inter font-bold text-white"
              isTitle={true}
            >
              Prêt à Commencer ?
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-amber-100 font-inter"
              delay={200}
            >
              Vous avez des questions sur nos formules ou besoin d&apos;une solution personnalisée ? Nous sommes là pour vous aider à trouver l&apos;option parfaite pour votre entreprise.
            </AnimatedText>
            <AnimatedText delay={400}>
              <ShimmerButton asChild size="lg" variant="secondary" className="bg-white text-amber-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">
                <Link to="/contact">Contactez-Nous Aujourd&apos;hui</Link>
              </ShimmerButton>
            </AnimatedText>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Plans;
